// Страница «Телеграм-бот» (раздел «Интеграции»): клубные настройки бота
// лояльности и уведомлений + регистрация вебхука в Telegram.
//
// Frontend-only: читает/пишет клубные key-value настройки через /v1/settings
// и регистрирует вебхук через /v1/telegram/set-webhook. Все настройки хранятся
// как строки; булевы тумблеры кодируются как '1'/'0'.

import { useEffect, useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import { Button, ErrorState, Field, Input, Loading, PageHead, SectionHead } from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'

/** Одна пара key/value из GET /v1/settings (мы используем только key+value). */
interface SettingRow {
  key: string
  value: string
}

/** Ответ POST /v1/telegram/set-webhook — сырой результат Telegram API. */
interface SetWebhookResult {
  ok: boolean
  description?: string
}

/** Ключи настроек, которые редактирует эта страница. */
const KEY_TOKEN = 'telegramBotToken'
const KEY_CHAT_ID = 'telegramChatId'
const KEY_NOTIFY_SHIFT_OPEN = 'telegramNotifyShiftOpen'
const KEY_NOTIFY_SHIFT_CLOSE = 'telegramNotifyShiftClose'
const KEY_NOTIFY_TARIFF_CHANGE = 'telegramNotifyTariffChange'
const KEY_NOTIFY_NEW_BOOKING = 'telegramNotifyNewBooking'
const KEY_NOTIFY_TABLE_SERVICE = 'telegramNotifyTableService'
const KEY_NOTIFY_REFUND = 'telegramNotifyRefund'

/**
 * Разбор строкового значения тумблера. По умолчанию (отсутствует / пусто) — ВКЛ.
 * Явно выключенными считаем '0', 'false', 'no', 'off' (без учёта регистра).
 */
function parseBool(value: string | undefined): boolean {
  if (value === undefined) return true
  const v = value.trim().toLowerCase()
  if (v === '') return true
  return !['0', 'false', 'no', 'off'].includes(v)
}

/** Кодирование булева тумблера в строку настройки. */
function encodeBool(on: boolean): string {
  return on ? '1' : '0'
}

export function Telegram(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Интеграции"
        title="Телеграм-бот"
        lead="Бот лояльности и уведомления клуба в Telegram: токен бота, чат для уведомлений о смене и тарифах, а также регистрация вебхука для приёма сообщений от Telegram."
      />
      <ClubGate>
        <TelegramEditor />
      </ClubGate>
    </>
  )
}

function TelegramEditor(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<SettingRow[]>('/v1/settings', query, [query?.clubId])

  // Локальное состояние формы.
  const [token, setToken] = useState('')
  const [chatId, setChatId] = useState('')
  const [notifyOpen, setNotifyOpen] = useState(true)
  const [notifyClose, setNotifyClose] = useState(true)
  const [notifyTariff, setNotifyTariff] = useState(true)
  const [notifyBooking, setNotifyBooking] = useState(true)
  const [notifyTableService, setNotifyTableService] = useState(true)
  const [notifyRefund, setNotifyRefund] = useState(true)
  const [saved, setSaved] = useState(false)

  const save = useMutate()
  const webhook = useMutate()
  const [webhookResult, setWebhookResult] = useState<SetWebhookResult | null>(null)

  // Заполняем форму из загруженных настроек (key→value).
  useEffect(() => {
    if (!list.data) return
    const map = new Map(list.data.map((r) => [r.key, r.value]))
    setToken(map.get(KEY_TOKEN) ?? '')
    setChatId(map.get(KEY_CHAT_ID) ?? '')
    setNotifyOpen(parseBool(map.get(KEY_NOTIFY_SHIFT_OPEN)))
    setNotifyClose(parseBool(map.get(KEY_NOTIFY_SHIFT_CLOSE)))
    setNotifyTariff(parseBool(map.get(KEY_NOTIFY_TARIFF_CHANGE)))
    setNotifyBooking(parseBool(map.get(KEY_NOTIFY_NEW_BOOKING)))
    setNotifyTableService(parseBool(map.get(KEY_NOTIFY_TABLE_SERVICE)))
    setNotifyRefund(parseBool(map.get(KEY_NOTIFY_REFUND)))
  }, [list.data])

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setSaved(false)

    // Пишем каждый ключ отдельным PUT /v1/settings/<key>.
    const puts: Array<[string, string]> = [
      [KEY_TOKEN, token.trim()],
      [KEY_CHAT_ID, chatId.trim()],
      [KEY_NOTIFY_SHIFT_OPEN, encodeBool(notifyOpen)],
      [KEY_NOTIFY_SHIFT_CLOSE, encodeBool(notifyClose)],
      [KEY_NOTIFY_TARIFF_CHANGE, encodeBool(notifyTariff)],
      [KEY_NOTIFY_NEW_BOOKING, encodeBool(notifyBooking)],
      [KEY_NOTIFY_TABLE_SERVICE, encodeBool(notifyTableService)],
      [KEY_NOTIFY_REFUND, encodeBool(notifyRefund)]
    ]

    const ok = await save.mutate(async () => {
      for (const [key, value] of puts) {
        await api.put(`/v1/settings/${encodeURIComponent(key)}`, { value }, { query })
      }
    })
    if (ok) {
      setSaved(true)
      list.reload()
    }
  }

  const onRegisterWebhook = async (): Promise<void> => {
    setWebhookResult(null)
    // Базовый URL бэкенда для итогового адреса вебхука.
    const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').trim()
    // Тело по SetWebhookDto: { baseUrl } (clubId для super_admin уходит в query).
    const ok = await webhook.mutate(async () => {
      const res = await api.post<SetWebhookResult>(
        '/v1/telegram/set-webhook',
        { baseUrl },
        { query }
      )
      setWebhookResult(res)
    })
    if (!ok) setWebhookResult(null)
  }

  if (list.loading) return <Loading />
  if (list.error) return <ErrorState message={list.error} />

  return (
    <>
      {/* ───────────── Настройки бота и уведомлений ───────────── */}
      <div className="card">
        <SectionHead title="Настройки бота" />
        <form id="telegram-form" onSubmit={(e) => void onSubmit(e)}>
          <div className="form-grid">
            <Field label="Токен бота" htmlFor="tg-token" full>
              <Input
                id="tg-token"
                type="password"
                autoComplete="off"
                value={token}
                onChange={(e) => {
                  setToken(e.target.value)
                  setSaved(false)
                }}
                placeholder="123456:ABC-DEF…"
                style={{ fontFamily: 'var(--f-mono)' }}
              />
              <span className="field__hint">
                Токен от @BotFather. Хранится в клубных настройках.
              </span>
            </Field>

            <Field label="Chat ID уведомлений" htmlFor="tg-chat" full>
              <Input
                id="tg-chat"
                value={chatId}
                onChange={(e) => {
                  setChatId(e.target.value)
                  setSaved(false)
                }}
                placeholder="напр. -1001234567890"
                style={{ fontFamily: 'var(--f-mono)' }}
              />
              <span className="field__hint">
                Чат, куда бот шлёт уведомления. Для групп — отрицательный ID.
              </span>
            </Field>

            <div className="field field--full">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={notifyOpen}
                  onChange={(e) => {
                    setNotifyOpen(e.target.checked)
                    setSaved(false)
                  }}
                />
                Уведомлять об открытии смены
              </label>
            </div>

            <div className="field field--full">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={notifyClose}
                  onChange={(e) => {
                    setNotifyClose(e.target.checked)
                    setSaved(false)
                  }}
                />
                Уведомлять о закрытии смены
              </label>
            </div>

            <div className="field field--full">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={notifyTariff}
                  onChange={(e) => {
                    setNotifyTariff(e.target.checked)
                    setSaved(false)
                  }}
                />
                Уведомлять об изменении тарифа
              </label>
            </div>

            <div className="field field--full">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={notifyBooking}
                  onChange={(e) => {
                    setNotifyBooking(e.target.checked)
                    setSaved(false)
                  }}
                />
                Уведомлять о новых бронях
              </label>
            </div>

            <div className="field field--full">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={notifyRefund}
                  onChange={(e) => {
                    setNotifyRefund(e.target.checked)
                    setSaved(false)
                  }}
                />
                Уведомлять о возвратах денег клиенту
              </label>
              <span className="field__hint">
                Сообщение уходит, когда кассир оформил возврат по чеку: номер возврата и чека,
                сумма, заведение, терминал и сотрудник. Если возврат пробивается по кассе, в
                уведомлении будет номер фискального чека возврата.
              </span>
            </div>

            <div className="field field--full">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={notifyTableService}
                  onChange={(e) => {
                    setNotifyTableService(e.target.checked)
                    setSaved(false)
                  }}
                />
                Уведомлять о постановке стола в сервис / тех. работы
              </label>
              <span className="field__hint">
                Сообщение уходит, когда оператор ставит стол в сервисный режим или на тех. работы —
                и когда снимает. Событие приезжает с терминала синхронизацией (обычно в пределах
                минуты).
              </span>
            </div>

            <div className="field field--full">
              <span className="field__hint">
                Напоминания об окончании подписки (за 10 и 5 дней, а также в день отключения)
                приходят в этот же чат всегда — отключить их нельзя, иначе доступ к терминалу
                заблокируется неожиданно. Автоматическое выставление счёта к напоминанию
                настраивается в разделе «Финансы».
              </span>
            </div>
          </div>

          {save.error && (
            <div className="form-error" style={{ marginTop: 14 }}>
              {save.error}
            </div>
          )}

          <div
            className="toolbar"
            style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <Button variant="primary" busy={save.saving} type="submit">
              Сохранить
            </Button>
            {saved && !save.saving && (
              <span className="muted" style={{ color: 'var(--green)' }}>
                ✓ Сохранено
              </span>
            )}
          </div>
        </form>
      </div>

      {/* ───────────── Регистрация вебхука ───────────── */}
      <div className="card">
        <SectionHead title="Вебхук Telegram" />
        <p className="muted" style={{ marginTop: 0 }}>
          Зарегистрируйте адрес вебхука в Telegram, чтобы бот принимал сообщения: команды лояльности
          клиентов (/start, /card), а также служебную кнопку «Текущие брони» и команды /bookings и
          /support (обращение в поддержку, ответ придёт сюда же) в чате уведомлений. Используется
          публичный адрес бэкенда. Для супер-админа вебхук регистрируется для клуба, выбранного в
          шапке.
        </p>
        <p className="muted" style={{ marginTop: 8 }}>
          После изменения набора функций бота (например, добавления кнопок) вебхук нужно
          зарегистрировать заново — иначе новые кнопки и команды работать не будут.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <Button
            variant="primary"
            busy={webhook.saving}
            type="button"
            onClick={() => void onRegisterWebhook()}
          >
            Зарегистрировать вебхук
          </Button>

          {webhookResult && (
            <span style={{ color: webhookResult.ok ? 'var(--green)' : 'var(--red)' }}>
              {webhookResult.ok ? '✓ ' : '✗ '}
              {webhookResult.ok
                ? 'Вебхук зарегистрирован'
                : (webhookResult.description ?? 'Не удалось зарегистрировать вебхук')}
            </span>
          )}
        </div>

        {webhook.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {webhook.error}
          </div>
        )}
      </div>
    </>
  )
}
